import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/shared/context/AuthContext';
import { usePermission } from '@/shared/hooks/usePermission';
import { prefetchRoute } from '@/shared/routing/routePrefetch';
import { useSpotlightHost } from '@/shared/layout/spotlight';
import { useCreateDialogHost } from '@/shared/layout/create-host';
import { isEditableTarget } from './isEditableTarget';
import { matchKeydown, type SequencePending } from './matchKeydown';
import { useShortcutsHost } from './ShortcutsHostContext';

/**
 * Global keyboard shortcuts (⌘K / ⌘/ + G/C sequences). Mount once under Layout.
 */
export const useKeyboardShortcuts = (): void => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasPermission } = usePermission();
  const { openSpotlight, isSpotlightOpen, canOpenSpotlight } = useSpotlightHost();
  const { tryOpenCreate } = useCreateDialogHost();
  const {
    toggleShortcutsHelp,
    isShortcutsHelpOpen,
    closeShortcutsHelp,
  } = useShortcutsHost();

  const pendingRef = useRef<SequencePending | null>(null);
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const result = matchKeydown({
        event,
        pending: pendingRef.current,
        now: Date.now(),
        hasPermission,
        isAdmin,
        inEditable: isEditableTarget(event.target),
        spotlightOpen: isSpotlightOpen,
        helpOpen: isShortcutsHelpOpen,
      });

      pendingRef.current = result.pending;

      if (result.type === 'pending') {
        event.preventDefault();
        return;
      }

      if (result.type !== 'match') return;

      const { shortcut } = result;

      if (shortcut.run.type === 'open-spotlight') {
        if (isSpotlightOpen) {
          event.preventDefault();
          return;
        }
        if (!canOpenSpotlight) return;
        event.preventDefault();
        closeShortcutsHelp();
        openSpotlight();
        return;
      }

      if (shortcut.run.type === 'open-help') {
        event.preventDefault();
        toggleShortcutsHelp();
        return;
      }

      if (shortcut.run.type === 'open-create') {
        event.preventDefault();
        closeShortcutsHelp();
        const opened = tryOpenCreate(shortcut.run.actionId, {
          state: shortcut.run.state,
          path: shortcut.run.path,
        });
        if (!opened && shortcut.run.path) {
          prefetchRoute(shortcut.run.path);
          navigate(
            shortcut.run.path,
            shortcut.run.state ? { state: shortcut.run.state } : undefined,
          );
        }
        return;
      }

      if (shortcut.run.type === 'navigate') {
        event.preventDefault();
        closeShortcutsHelp();
        prefetchRoute(shortcut.run.path);
        navigate(
          shortcut.run.path,
          shortcut.run.state ? { state: shortcut.run.state } : undefined,
        );
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    hasPermission,
    isAdmin,
    isSpotlightOpen,
    isShortcutsHelpOpen,
    canOpenSpotlight,
    openSpotlight,
    tryOpenCreate,
    toggleShortcutsHelp,
    closeShortcutsHelp,
    navigate,
  ]);
};
